/**
 * Toolbar above the connections table: status toggle, organization search and
 * vendor select. Filtering itself happens in MonitoringList.
 */
import {
  Box,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import type { EhrSyncStatus } from '@models/ehr-sync';

import { statusMessages } from '../../messages';
import { STATUS_FILTERS } from '../shared/monitoring.util';

const messages = defineMessages({
  all: { id: 'ehrSync.list.filter.all', defaultMessage: 'All' },
  filterByStatus: {
    id: 'ehrSync.list.filter.byStatus',
    defaultMessage: 'Filter by status',
  },
  searchOrganizations: {
    id: 'ehrSync.list.filter.searchOrganizations',
    defaultMessage: 'Search organizations',
  },
  vendor: { id: 'ehrSync.list.filter.vendor', defaultMessage: 'Vendor' },
  allVendors: {
    id: 'ehrSync.list.filter.allVendors',
    defaultMessage: 'All vendors',
  },
});

export type StatusFilter = EhrSyncStatus | 'all';
/** 'all' or a vendor name. */
export type VendorFilter = string;

interface FilterBarProps {
  status: StatusFilter;
  onStatus: (status: StatusFilter) => void;
  query: string;
  onQuery: (query: string) => void;
  vendor: VendorFilter;
  vendors: string[];
  onVendor: (vendor: VendorFilter) => void;
}

export default function FilterBar({
  status,
  onStatus,
  query,
  onQuery,
  vendor,
  vendors,
  onVendor,
}: FilterBarProps) {
  const { formatMessage } = useIntl();

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ md: 'center' }}
      sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status}
        onChange={(_event, value: StatusFilter | null) => {
          if (value) {
            onStatus(value);
          }
        }}
        aria-label={formatMessage(messages.filterByStatus)}
      >
        {STATUS_FILTERS.map((option) => (
          <ToggleButton key={option} value={option}>
            {option === 'all'
              ? formatMessage(messages.all)
              : formatMessage(statusMessages[option])}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ flex: 1 }} />

      <TextField
        size="small"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        placeholder={formatMessage(messages.searchOrganizations)}
        inputProps={{
          'aria-label': formatMessage(messages.searchOrganizations),
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon name="magnifying-glass" color="text.secondary" type="fas" />
            </InputAdornment>
          ),
        }}
        sx={{ width: { xs: '100%', md: 260 } }}
      />

      <TextField
        select
        size="small"
        label={formatMessage(messages.vendor)}
        value={vendor}
        onChange={(event) => onVendor(event.target.value)}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="all">{formatMessage(messages.allVendors)}</MenuItem>
        {vendors.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}
